import { useEffect, useRef, useState } from 'react';
import { IPublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { CaseInfo, CaseStatus } from '../dto/model';
import { GetAccessToken } from '../utils/auth-utils';
import { scopes } from '../authConfig';
import { wait } from '../services/utils';

type UseCallRecordingParams = {
  isOnline: boolean;
  userShouldBeRecorded: boolean;
  isAutodialerFlow: boolean;
  salesChannel: string | undefined;
  caseInfo: CaseInfo | null;
  instance: IPublicClientApplication;
  inProgress: InteractionStatus;
};

export type CallRecording = {
  /**
   * Stops the active recorder and uploads its audio for the current case. The identifiers,
   * sales channel and outcome are captured synchronously at call time, so a later case
   * re-assignment can never re-tag this recording. Use when a case is booked/lost/closed.
   */
  reportOutcome: (status: CaseStatus) => void;
  /** Stops and drops the active recording without uploading. Use when the agent goes offline. */
  stopAndDiscard: () => void;
  /** Resets to the idle state so a new recording can start on the next online case. */
  reset: () => void;
  /** True only while the microphone is actively capturing audio (drives the recording badge). */
  isRecording: boolean;
};

/** A live browser recorder plus the stream and the chunks it is accumulating. */
type ActiveRecorder = {
  recorder: MediaRecorder;
  stream: MediaStream;
  chunks: Blob[];
};

/** Everything needed to upload one recording, snapshotted synchronously at outcome time. */
type RecordingUpload = {
  bookingStatus: string;
  salesChannel: string | undefined;
  signupId: string | undefined;
};

const UPLOAD_MAX_ATTEMPTS = 3;

const isRetryableUploadStatus = (status: number): boolean =>
  status === 408 || status === 429 || status >= 500;

/**
 * `useCallRecording` owns the browser `MediaRecorder` lifecycle for agent call recording:
 * it starts recording whenever a `Recorded`-role agent is online with no active case (any flow
 * or sales channel), and on a case outcome it stops the recorder and uploads the audio to the
 * transcription service keyed on the signup id.
 *
 * Stop + upload are driven imperatively from `reportOutcome`, which snapshots the case
 * identifiers before stopping the recorder, so the audio can never be attributed to a later
 * customer even if `MediaRecorder.onstop` is delayed.
 */
export function useCallRecording({
  isOnline,
  userShouldBeRecorded,
  isAutodialerFlow,
  salesChannel,
  caseInfo,
  instance,
  inProgress,
}: UseCallRecordingParams): CallRecording {
  const recorderRef = useRef<ActiveRecorder | null>(null);
  const recordingRequestSequence = useRef(0);
  // Cross-tab coordination: only one recording should be active per user/browser. Each hook instance
  // (tab) has a unique id and announces on a shared BroadcastChannel when it starts recording; other
  // tabs stop their active recording and upload it as ReachedButNotBooked.
  const channelRef = useRef<BroadcastChannel | null>(null);
  const tabIdRef = useRef<string>('');
  if (!tabIdRef.current) {
    tabIdRef.current =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${new Date().getTime()}-${Math.random()}`;
  }
  const sttRootUrl = (process as any).env.REACT_APP_STT_ROOT_URL;
  // `caseStatus` is only a lifecycle gate for (re)starting the recorder; it no longer drives
  // the upload. None = idle (recorder may run); any other value = a case is being finalized.
  const [caseStatus, setCaseStatus] = useState<CaseStatus>(CaseStatus.None);
  // Reflects whether the microphone is actively capturing; drives the recording indicator so it
  // disappears as soon as recording stops (case closed/booked), not merely when the agent is online.
  const [isRecording, setIsRecording] = useState(false);

  // Capture the assigned case's signup id while caseInfo is available. Keep it when caseInfo is
  // cleared during completion, but overwrite it (including with undefined) as soon as another case
  // is assigned so an identifier can never leak from one customer's recording to the next.
  const recordingIdentifierRef = useRef<string | undefined>(undefined);
  if (caseInfo) {
    recordingIdentifierRef.current = caseInfo.signupId?.trim() || undefined;
  }

  const shouldSkipRecordingForStatus = (status: CaseStatus) =>
    isAutodialerFlow ? status === CaseStatus.WrongNumber : status === CaseStatus.NotReached || status === CaseStatus.WrongNumber;

  // Holds the latest reportOutcome so the once-mounted BroadcastChannel listener always calls the
  // current closure (which snapshots the live caseInfo/salesChannel) without re-subscribing.
  const reportOutcomeRef = useRef<(status: CaseStatus) => void>(() => {});

  // Set up cross-tab coordination once. When another tab announces it started recording, stop and
  // upload this tab's active recording as ReachedButNotBooked (the agent has moved on to a new page).
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('enpal-call-recording');
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'recording-started'
        && event.data?.tabId !== tabIdRef.current
        && recorderRef.current
      ) {
        reportOutcomeRef.current(CaseStatus.ReachedButNotBooked);
      }
    };
    return () => {
      channel.onmessage = null;
      channel.close();
      channelRef.current = null;
    };
  }, []);

  // Start recording when a Recorded-role agent is online with no active case (any flow/channel).
  useEffect(() => {
    const requestSequence = ++recordingRequestSequence.current;

    if (userShouldBeRecorded && isOnline && caseStatus === CaseStatus.None && !recorderRef.current) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          if (requestSequence !== recordingRequestSequence.current) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          const recorder = new MediaRecorder(stream);
          const chunks: Blob[] = [];
          recorder.ondataavailable = (event: BlobEvent) => {
            chunks.push(event.data);
          };
          recorder.start();

          recorderRef.current = { recorder, stream, chunks };
          setIsRecording(true);
          // Tell other tabs to stop their recording — only one recording is active per user.
          channelRef.current?.postMessage({ type: 'recording-started', tabId: tabIdRef.current });
        })
        .catch((error) => {
          if (requestSequence === recordingRequestSequence.current) {
            console.error('Error accessing microphone:', error);
          }
        });
    }

    return () => {
      if (recordingRequestSequence.current === requestSequence) {
        recordingRequestSequence.current += 1;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseStatus, isOnline, userShouldBeRecorded]);

  // Stop the recorder and release the microphone if the hook unmounts (e.g. navigation) without
  // an explicit stopAndDiscard/reportOutcome.
  useEffect(() => {
    return () => {
      releaseRecorder(takeActiveRecorder());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const takeActiveRecorder = (): ActiveRecorder | null => {
    const active = recorderRef.current;
    recorderRef.current = null;
    return active;
  };

  /** Stops the recorder and all its media tracks without uploading. */
  const releaseRecorder = (active: ActiveRecorder | null) => {
    setIsRecording(false);
    if (!active) return;
    active.recorder.onstop = null;
    try {
      if (active.recorder.state !== 'inactive') {
        active.recorder.stop();
      }
    } catch (error) {
      console.error('Failed to stop recorder:', error);
    }
    active.stream.getTracks().forEach((track) => track.stop());
  };

  function getCaseStatusString(status: CaseStatus): string {
    switch (status) {
      case CaseStatus.None:
        return 'None';
      case CaseStatus.Booked:
        return 'Booked';
      case CaseStatus.ReachedButNotBooked:
        return 'ReachedButNotBooked';
      case CaseStatus.NotReached:
        return 'NotReached';
      case CaseStatus.CustomerLost:
        return 'CustomerLost';
      case CaseStatus.WrongNumber:
        return 'WrongNumber';
      default:
        return 'Unknown';
    }
  }

  const uploadRecording = async (payload: RecordingUpload, chunks: Blob[]) => {
    const hasSignupId = typeof payload.signupId === 'string' && payload.signupId.trim().length > 0;

    // A signup id is useful attribution metadata, but it is not required to submit captured audio.
    if (!payload.bookingStatus || chunks.length === 0) {
      return;
    }

    let audioBlob: Blob;
    let accessToken: string;
    let userId: string;
    try {
      audioBlob = new Blob(chunks, { type: 'audio/wav' });
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      accessToken = token.accessToken;
      userId = token.account.localAccountId;
    } catch (error) {
      console.error('Failed to prepare recording upload:', error);
      return;
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      'enpal-user-id': userId,
      'enpal-correlation-id': '',
      'enpal-language-locale': 'de-DE',
      'enpal-booking-state': payload.bookingStatus,
      'enpal-sales-channel': payload.salesChannel ?? '',
      'Content-Type': 'application/octet-stream',
    };
    if (hasSignupId) {
      headers['enpal-signup-id'] = payload.signupId!;
    }

    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(`${sttRootUrl}/api/v1/calls/transcriptions`, {
          method: 'POST',
          headers,
          body: audioBlob,
        });
        if (response.ok) {
          return;
        }
        console.error(`Recording upload failed with HTTP ${response.status} (attempt ${attempt}/${UPLOAD_MAX_ATTEMPTS})`);
        if (!isRetryableUploadStatus(response.status)) {
          return;
        }
      } catch (error) {
        console.error(`Recording upload network error (attempt ${attempt}/${UPLOAD_MAX_ATTEMPTS}):`, error);
      }

      if (attempt < UPLOAD_MAX_ATTEMPTS) {
        await wait(attempt * 1000);
      }
    }

    console.error('Recording upload permanently failed; audio was not delivered.');
  };

  const reportOutcome = (status: CaseStatus) => {
    const active = takeActiveRecorder();
    // Mark the case as finalized so the start effect does not immediately re-record; reset()
    // returns to None when the next case begins.
    setCaseStatus(status);

    if (!active) return;

    // Skipped outcomes (e.g. NotReached/WrongNumber) are not worth transcribing: drop the audio.
    if (shouldSkipRecordingForStatus(status)) {
      releaseRecorder(active);
      return;
    }
    const payload: RecordingUpload = {
      bookingStatus: getCaseStatusString(status),
      salesChannel,
      // Use the signup id captured at assignment rather than the live caseInfo, which may already be
      // cleared by the time the recording stops.
      signupId: recordingIdentifierRef.current,
    };

    setIsRecording(false);
    active.recorder.onstop = () => {
      active.stream.getTracks().forEach((track) => track.stop());
      void uploadRecording(payload, active.chunks);
    };
    try {
      active.recorder.requestData();
      active.recorder.stop();
    } catch (error) {
      active.recorder.onstop = null;
      active.stream.getTracks().forEach((track) => track.stop());
      console.error('Failed to stop recorder for upload:', error);
    }
  };

  reportOutcomeRef.current = reportOutcome;

  const stopAndDiscard = () => {
    releaseRecorder(takeActiveRecorder());
  };

  const reset = () => {
    setCaseStatus(CaseStatus.None);
    // Clear so the next case captures its own identifier rather than reusing the previous case's.
    recordingIdentifierRef.current = undefined;
  };

  return { reportOutcome, stopAndDiscard, reset, isRecording };
}
